// ─────────────────────────────────────────────────────────────────────────────
// Procedural GSAP rig for the avatar.
//
// The SVG is a forward-kinematics rig: each limb segment is a <g> whose
// rotation pivots on an exact joint coordinate (via GSAP's svgOrigin).
// Gestures are timeline factories that rotate joints with staggered offsets —
// shoulder leads, elbow follows, wrist lags — which is what makes the motion
// read as intentional rather than tweened.
//
// Structure follows the shimeji/Clippy pattern: actions (gestures) are defined
// here, behaviors (when to play them) are scheduled by the component.
// ─────────────────────────────────────────────────────────────────────────────
import gsap from 'gsap'

// Joint pivots in viewBox (0 0 200 240) coordinates
const JOINTS = {
  root:      '100 228',
  head:      '100 106',
  antenna:   '100 46',
  eyeL:      '83 74',
  eyeR:      '117 74',
  shoulderL: '74 124',
  elbowL:    '60 142',
  wristL:    '58 156',
  shoulderR: '126 124',
  elbowR:    '140 142',
  wristR:    '142 156',
  jet:       '100 196',
  shadow:    '100 228',
}

const MOUTHS = {
  smile: 'M92,89.5 Q100,92.5 108,86.5',
  grin:  'M90,87.5 Q100,96 110,86.5',
  hmm:   'M93,90 Q100,90.8 107,90',
  oh:    'M96,87.5 Q100,92.5 104,87.5',
}

export const createAvatarRig = (svg, { reducedMotion = false } = {}) => {
  const $ = (part) => svg.querySelector(`[data-part="${part}"]`)

  const p = {
    root:     $('root'),
    float:    $('float'),
    shadow:   $('shadow'),
    head:     $('head'),
    antenna:  $('antenna'),
    tip:      $('antenna-tip'),
    eyeL:     $('eye-l'),
    eyeR:     $('eye-r'),
    pupils:   svg.querySelectorAll('[data-part="pupil"]'),
    browL:    $('brow-l'),
    browR:    $('brow-r'),
    mouth:    $('mouth'),
    armL:     $('arm-l'),
    forearmL: $('forearm-l'),
    handL:    $('hand-l'),
    armR:     $('arm-r'),
    forearmR: $('forearm-r'),
    handR:    $('hand-r'),
    jet:      $('jet'),
  }

  // Pin every joint to its anatomical pivot
  gsap.set(p.root,     { svgOrigin: JOINTS.root })
  gsap.set(p.head,     { svgOrigin: JOINTS.head })
  gsap.set(p.antenna,  { svgOrigin: JOINTS.antenna })
  gsap.set(p.eyeL,     { svgOrigin: JOINTS.eyeL })
  gsap.set(p.eyeR,     { svgOrigin: JOINTS.eyeR })
  gsap.set(p.armL,     { svgOrigin: JOINTS.shoulderL })
  gsap.set(p.forearmL, { svgOrigin: JOINTS.elbowL })
  gsap.set(p.handL,    { svgOrigin: JOINTS.wristL })
  gsap.set(p.armR,     { svgOrigin: JOINTS.shoulderR })
  gsap.set(p.forearmR, { svgOrigin: JOINTS.elbowR })
  gsap.set(p.handR,    { svgOrigin: JOINTS.wristR })
  gsap.set(p.jet,      { svgOrigin: JOINTS.jet })
  gsap.set(p.shadow,   { svgOrigin: JOINTS.shadow })

  const ambient = []           // persistent loops, killed on destroy
  let busy = false             // a gesture is playing — idles wait their turn
  let blinkCall = null
  let saccadeCall = null
  let mouseLookUntil = 0

  // Damped setters for cursor tracking
  const pupilX = gsap.quickTo(p.pupils, 'x', { duration: 0.35, ease: 'power2.out' })
  const pupilY = gsap.quickTo(p.pupils, 'y', { duration: 0.35, ease: 'power2.out' })
  const headTilt = gsap.quickTo(p.head, 'rotation', { duration: 0.6, ease: 'power2.out' })

  // ── Ambient life ───────────────────────────────────────────────────────────

  const startAmbient = () => {
    if (reducedMotion) return

    // Hover bob, with the shadow breathing in counterpoint
    ambient.push(
      gsap.to(p.float, { y: -6, duration: 2.6, ease: 'sine.inOut', repeat: -1, yoyo: true }),
      gsap.to(p.shadow, {
        scaleX: 0.88, opacity: 0.12,
        duration: 2.6, ease: 'sine.inOut', repeat: -1, yoyo: true,
      }),
      // Thruster flicker — new random flame height every tick
      gsap.to(p.jet, {
        scaleY: 'random(0.65, 1.25)', opacity: 'random(0.55, 0.95)',
        duration: 0.16, repeat: -1, repeatRefresh: true, ease: 'none',
      }),
      // The antenna never quite sits still
      gsap.to(p.antenna, { rotation: 3, duration: 3.4, ease: 'sine.inOut', repeat: -1, yoyo: true })
    )

    scheduleBlink()
    scheduleSaccade()
  }

  const scheduleBlink = () => {
    blinkCall = gsap.delayedCall(gsap.utils.random(2.4, 6.5), () => {
      const doubleBlink = Math.random() < 0.22
      const tl = gsap.timeline({ onComplete: scheduleBlink })
      tl.to([p.eyeL, p.eyeR], { scaleY: 0.06, duration: 0.07, ease: 'power2.in' })
        .to([p.eyeL, p.eyeR], { scaleY: 1, duration: 0.1, ease: 'power2.out' })
      if (doubleBlink) {
        tl.to([p.eyeL, p.eyeR], { scaleY: 0.06, duration: 0.07 }, '+=0.09')
          .to([p.eyeL, p.eyeR], { scaleY: 1, duration: 0.1 })
      }
    })
  }

  // Small darting eye movements when the cursor hasn't spoken recently
  const scheduleSaccade = () => {
    saccadeCall = gsap.delayedCall(gsap.utils.random(1.6, 4.2), () => {
      if (Date.now() > mouseLookUntil && !busy) {
        pupilX(gsap.utils.random(-3, 3))
        pupilY(gsap.utils.random(-2, 2))
      }
      scheduleSaccade()
    })
  }

  // nx, ny in [-1, 1] — cursor position relative to the avatar
  const lookAt = (nx, ny) => {
    if (reducedMotion) return
    mouseLookUntil = Date.now() + 1200
    pupilX(nx * 3.5)
    pupilY(ny * 2.5)
    if (!busy) headTilt(nx * 5)
  }

  // ── Gesture helpers ────────────────────────────────────────────────────────

  const gesture = (build, { force = false } = {}) => {
    if (busy && !force) return null
    busy = true
    const tl = build()
    tl.eventCallback('onComplete', () => { busy = false })
    tl.eventCallback('onInterrupt', () => { busy = false })
    return tl
  }

  const setMouth = (name, duration = 0.25) =>
    gsap.to(p.mouth, { attr: { d: MOUTHS[name] }, duration, ease: 'power2.inOut' })

  const neutralize = (tl, at = '>') => {
    tl.to([p.armR, p.forearmR, p.handR, p.armL, p.forearmL, p.handL], {
      rotation: 0, duration: 0.55, ease: 'power3.inOut', stagger: 0.04,
    }, at)
    tl.to(p.head, { rotation: 0, duration: 0.5, ease: 'power2.inOut' }, '<')
    tl.to([p.browL, p.browR], { y: 0, rotation: 0, duration: 0.4 }, '<')
    tl.add(setMouth('smile'), '<')
    // Follow-through: the antenna keeps moving after the body stops
    tl.to(p.antenna, { rotation: 0, duration: 1.1, ease: 'elastic.out(1.1, 0.28)' }, '<0.1')
    return tl
  }

  // ── Named gestures ─────────────────────────────────────────────────────────

  /** Big greeting wave with the screen-right arm. */
  const wave = () => gesture(() => {
    const tl = gsap.timeline()
    // Anticipation: a beat of pull-back before the arm swings up
    tl.to(p.armR, { rotation: 10, duration: 0.14, ease: 'power2.in' })
    tl.to(p.head, { rotation: -7, duration: 0.4, ease: 'power2.out' }, 0)
    tl.to([p.browL, p.browR], { y: -3, duration: 0.3 }, 0)
    tl.add(setMouth('grin', 0.3), 0)
    // Raise: shoulder leads, elbow and wrist follow — stopping short of
    // vertical so the drop-hand waves BESIDE the helmet, not across the visor
    tl.to(p.armR,     { rotation: -132, duration: 0.42, ease: 'back.out(1.4)' }, 0.14)
    tl.to(p.forearmR, { rotation: -30,  duration: 0.36, ease: 'back.out(2)' },   0.22)
    tl.to(p.handR,    { rotation: -12,  duration: 0.3,  ease: 'back.out(2)' },   0.3)
    // The wave itself: forearm oscillates, wrist lags a breath behind
    tl.to(p.forearmR, { rotation: 20, duration: 0.18, ease: 'sine.inOut', repeat: 5, yoyo: true }, 0.62)
    tl.to(p.handR,    { rotation: 18, duration: 0.18, ease: 'sine.inOut', repeat: 5, yoyo: true }, 0.68)
    // Body leans into the greeting
    tl.to(p.float, { rotation: 4, duration: 0.5, ease: 'sine.inOut' }, 0.4)
    tl.to(p.float, { rotation: 0, duration: 0.6, ease: 'sine.inOut' }, 1.5)
    return neutralize(tl, 1.85)
  }, { force: true })

  /** Double-bicep flex with a grin and two pumps. The signature brag. */
  const flex = () => gesture(() => {
    const tl = gsap.timeline()
    tl.add(setMouth('grin', 0.2), 0)
    tl.to([p.browL, p.browR], { y: -2.5, duration: 0.25 }, 0)
    // Curl up: shoulders lead, gauntlets snap into the flex
    tl.to(p.armR,     { rotation: -100, duration: 0.38, ease: 'back.out(1.6)' }, 0)
    tl.to(p.armL,     { rotation: 100,  duration: 0.38, ease: 'back.out(1.6)' }, 0.06)
    tl.to(p.forearmR, { rotation: -115, duration: 0.34, ease: 'back.out(1.8)' }, 0.1)
    tl.to(p.forearmL, { rotation: 115,  duration: 0.34, ease: 'back.out(1.8)' }, 0.16)
    // Pump it — fists and frame bounce together
    tl.to(p.forearmR, { rotation: -103, duration: 0.22, ease: 'sine.inOut', repeat: 3, yoyo: true }, 0.55)
    tl.to(p.forearmL, { rotation: 103,  duration: 0.22, ease: 'sine.inOut', repeat: 3, yoyo: true }, 0.55)
    tl.to(p.root, { scaleY: 0.97, scaleX: 1.02, duration: 0.22, ease: 'sine.inOut', repeat: 3, yoyo: true }, 0.55)
    tl.to({}, { duration: 0.25 })
    return neutralize(tl)
  })

  /** Quick 360 showboat spin with squash-and-stretch landing. */
  const spin = () => gesture(() => {
    const tl = gsap.timeline()
    tl.add(setMouth('grin', 0.15), 0)
    tl.to(p.root, { scaleX: 1.06, scaleY: 0.94, duration: 0.16, ease: 'power2.in' })
    tl.to(p.float, { rotation: 360, duration: 0.72, ease: 'power2.inOut' }, 0.12)
    tl.set(p.float, { rotation: 0 })
    tl.to(p.root, { scaleX: 1, scaleY: 1, duration: 0.55, ease: 'elastic.out(1.2, 0.4)' }, 0.6)
    tl.to({}, { duration: 0.2 })
    return neutralize(tl)
  })

  /** Quick acknowledging nod. */
  const nod = () => gesture(() => {
    const tl = gsap.timeline()
    tl.to(p.head, { rotation: 0, y: 4, duration: 0.16, ease: 'power2.in', repeat: 3, yoyo: true })
    tl.to(p.head, { y: 0, duration: 0.2 })
    return tl
  })

  /** Present the fact bubble — open-handed maître-d' sweep, clear of the face. */
  const point = () => gesture(() => {
    const tl = gsap.timeline()
    tl.to(p.armR,     { rotation: -122, duration: 0.4, ease: 'back.out(1.6)' })
    tl.to(p.forearmR, { rotation: -32,  duration: 0.34, ease: 'back.out(2)' }, 0.08)
    tl.to(p.head,     { rotation: -8,   duration: 0.35, ease: 'power2.out' }, 0)
    tl.to([p.browL, p.browR], { y: -3, duration: 0.3 }, 0)
    tl.add(setMouth('oh', 0.25), 0)
    tl.to({}, { duration: 1.1 })                       // hold the pose
    return neutralize(tl)
  })

  /** Both arms up — celebration when the chat comes back. */
  const cheer = () => gesture(() => {
    const tl = gsap.timeline()
    tl.to(p.armR, { rotation: -145, duration: 0.32, ease: 'back.out(1.7)' }, 0)
    tl.to(p.armL, { rotation: 145,  duration: 0.32, ease: 'back.out(1.7)' }, 0.05)
    tl.to([p.forearmR], { rotation: -20, duration: 0.28, ease: 'back.out(2)' }, 0.1)
    tl.to([p.forearmL], { rotation: 20,  duration: 0.28, ease: 'back.out(2)' }, 0.15)
    tl.to(p.float, { y: -16, duration: 0.26, ease: 'power2.out' }, 0.05)
    tl.to(p.float, { y: 0, duration: 0.5, ease: 'bounce.out' }, 0.31)
    tl.add(setMouth('grin', 0.2), 0)
    tl.to({}, { duration: 0.4 })
    return neutralize(tl)
  })

  /**
   * Drift along the bottom edge of the screen, look around, drift home.
   * Shimeji-style: stays on the edge so it never blocks content.
   */
  const wander = (container) => gesture(() => {
    const roam = -gsap.utils.random(140, Math.min(window.innerWidth * 0.38, 460))
    const tl = gsap.timeline()
    // Lean into the direction of travel
    tl.to(p.float, { rotation: -6, duration: 0.4, ease: 'sine.out' }, 0)
    tl.to(container, { x: roam, duration: gsap.utils.random(2.6, 3.6), ease: 'sine.inOut' }, 0)
    tl.to(p.float, { rotation: 0, duration: 0.5, ease: 'sine.out' }, '>-0.4')
    // Have a look around the new spot
    tl.to(p.pupils, { x: -3, duration: 0.3 }, '<')
    tl.to(p.head, { rotation: -6, duration: 0.4 }, '<')
    tl.to(p.pupils, { x: 3, duration: 0.4 }, '+=0.45')
    tl.to(p.head, { rotation: 6, duration: 0.5 }, '<')
    tl.to([p.pupils, p.head], { x: 0, rotation: 0, duration: 0.35 }, '+=0.3')
    tl.to({}, { duration: gsap.utils.random(0.5, 1.4) })
    // Head home, leaning the other way
    tl.to(p.float, { rotation: 6, duration: 0.4, ease: 'sine.out' })
    tl.to(container, { x: 0, duration: gsap.utils.random(2.2, 3.2), ease: 'sine.inOut' }, '<')
    tl.to(p.float, { rotation: 0, duration: 0.5, ease: 'sine.out' }, '>-0.4')
    return tl
  })

  /** Looping "thinking" pose while the AI is answering. */
  let thinkTl = null
  const setThinking = (on) => {
    if (reducedMotion) return
    if (on && !thinkTl) {
      busy = true
      thinkTl = gsap.timeline()
      thinkTl.to(p.head, { rotation: 7, duration: 0.5, ease: 'power2.inOut' }, 0)
      thinkTl.to(p.pupils, { x: -3, y: -2.5, duration: 0.4 }, 0)
      thinkTl.to(p.browL, { y: 2, rotation: -6, duration: 0.4 }, 0)
      thinkTl.to(p.browR, { y: -2.5, rotation: 6, duration: 0.4 }, 0)
      thinkTl.add(setMouth('hmm', 0.3), 0)
      // Hand drifts up toward the chin
      thinkTl.to(p.armR,     { rotation: -115, duration: 0.5, ease: 'power3.out' }, 0.05)
      thinkTl.to(p.forearmR, { rotation: -58,  duration: 0.45, ease: 'power3.out' }, 0.15)
      // Antenna tip pulses like a status light
      thinkTl.to(p.tip, { scale: 1.35, opacity: 0.6, svgOrigin: '100 26', duration: 0.35, ease: 'sine.inOut', repeat: -1, yoyo: true }, 0.4)
    } else if (!on && thinkTl) {
      thinkTl.kill()
      thinkTl = null
      gsap.to(p.tip, { scale: 1, opacity: 1, duration: 0.3 })
      gsap.to(p.pupils, { x: 0, y: 0, duration: 0.3 })
      const tl = gsap.timeline()
      neutralize(tl, 0)
      tl.eventCallback('onComplete', () => { busy = false })
    }
  }

  // Idle micro-gestures — the pool the scheduler picks from
  const idlePool = [
    // Curious glance off to one side
    () => gesture(() => {
      const dir = Math.random() < 0.5 ? 1 : -1
      const tl = gsap.timeline()
      tl.to(p.head, { rotation: 9 * dir, duration: 0.45, ease: 'power2.inOut' })
      tl.to(p.pupils, { x: 3 * dir, duration: 0.3 }, 0.1)
      tl.to({}, { duration: 0.9 })
      tl.to(p.head, { rotation: 0, duration: 0.5, ease: 'power2.inOut' })
      tl.to(p.pupils, { x: 0, duration: 0.3 }, '<')
      return tl
    }),
    // Antenna boing
    () => gesture(() => {
      const tl = gsap.timeline()
      tl.to(p.antenna, { rotation: -22, duration: 0.16, ease: 'power2.in' })
      tl.to(p.antenna, { rotation: 0, duration: 1.4, ease: 'elastic.out(1.2, 0.2)' })
      return tl
    }),
    // Slow sway with a body twist
    () => gesture(() => {
      const tl = gsap.timeline()
      tl.to(p.float, { rotation: -4, duration: 0.8, ease: 'sine.inOut' })
      tl.to(p.armL, { rotation: 8, duration: 0.8, ease: 'sine.inOut' }, 0)
      tl.to(p.armR, { rotation: 8, duration: 0.8, ease: 'sine.inOut' }, 0)
      tl.to([p.float, p.armL, p.armR], { rotation: 0, duration: 0.9, ease: 'sine.inOut' })
      return tl
    }),
    // Little hop of restlessness
    () => gesture(() => {
      const tl = gsap.timeline()
      tl.to(p.root, { scaleX: 1.05, scaleY: 0.94, duration: 0.14, ease: 'power2.in' })
      tl.to(p.root, { scaleX: 1, scaleY: 1, duration: 0.5, ease: 'elastic.out(1.2, 0.35)' })
      tl.to(p.float, { y: -13, duration: 0.24, ease: 'power2.out' }, 0.12)
      tl.to(p.float, { y: 0, duration: 0.45, ease: 'bounce.out' }, 0.36)
      return tl
    }),
  ]

  const playRandomIdleGesture = () => {
    if (busy) return null
    // Every so often the idle is a full showboat move
    if (Math.random() < 0.18) return Math.random() < 0.6 ? flex() : spin()
    return idlePool[Math.floor(Math.random() * idlePool.length)]()
  }

  // ── Entrances and exits (container-level, in screen px) ───────────────────

  /**
   * Pop up from the bottom corner. First time: periscope peek — eyes scan
   * the room, a tiny anticipatory duck, then a spring up with squash-and-
   * stretch. Re-entries skip the theatrics.
   */
  const enter = (container, { firstTime = true } = {}) => {
    gsap.killTweensOf(container)
    if (reducedMotion) {
      gsap.set(container, { x: 0, y: 0, autoAlpha: 1 })
      return gsap.timeline() // empty, resolves immediately
    }
    busy = true
    const tl = gsap.timeline({ onComplete: () => { busy = false } })
    tl.set(container, { x: 0, y: 240, autoAlpha: 1 })
    if (firstTime) {
      // Periscope: just the eyes over the edge
      tl.to(container, { y: 112, duration: 0.65, ease: 'power2.out' })
      tl.to(p.pupils, { x: -3, duration: 0.22 }, '+=0.12')
      tl.to(p.pupils, { x: 3, duration: 0.3 }, '+=0.18')
      tl.to(p.pupils, { x: 0, duration: 0.2 }, '+=0.12')
      // Anticipation dip, then the spring
      tl.to(container, { y: 122, duration: 0.16, ease: 'power2.in' })
      tl.to(container, { y: 0, duration: 0.7, ease: 'back.out(1.7)' })
    } else {
      tl.to(container, { y: 0, duration: 0.55, ease: 'back.out(1.5)' })
    }
    // Squash-and-stretch settle on arrival
    tl.to(p.root, { scaleY: 0.92, scaleX: 1.07, duration: 0.1, ease: 'power2.in' }, '-=0.12')
    tl.to(p.root, { scaleY: 1, scaleX: 1, duration: 0.55, ease: 'elastic.out(1.1, 0.4)' })
    return tl
  }

  const exit = (container) => {
    gsap.killTweensOf(container)
    busy = true
    const tl = gsap.timeline({ onComplete: () => { busy = false } })
    if (reducedMotion) {
      tl.to(container, { autoAlpha: 0, duration: 0.2 })
      return tl
    }
    // A little hop of anticipation, then drop back below the edge
    tl.to(p.float, { y: -8, duration: 0.16, ease: 'power2.out' })
    tl.to(p.float, { y: 0, duration: 0.2, ease: 'power2.in' })
    tl.to(container, { y: 240, duration: 0.5, ease: 'power3.in' }, 0.12)
    tl.set(container, { autoAlpha: 0 })
    return tl
  }

  const destroy = () => {
    ambient.forEach((t) => t.kill())
    blinkCall?.kill()
    saccadeCall?.kill()
    thinkTl?.kill()
    gsap.killTweensOf([
      p.root, p.float, p.shadow, p.head, p.antenna, p.tip, p.eyeL, p.eyeR,
      p.pupils, p.browL, p.browR, p.mouth,
      p.armL, p.forearmL, p.handL, p.armR, p.forearmR, p.handR, p.jet,
    ])
  }

  return {
    startAmbient, lookAt, wave, flex, spin, nod, point, cheer, wander, setThinking,
    playRandomIdleGesture, enter, exit, destroy,
    get busy() { return busy },
  }
}
